/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { type ComponentType, useContext, useEffect, useState } from 'react';

import { FormattedMessage } from 'react-intl-next';

import { cssMap, jsx } from '@atlaskit/css';
import Lozenge from '@atlaskit/lozenge';
import { Text } from '@atlaskit/primitives/compiled';

import type { InvitedAppShortcutAllowedApsKey } from '../../../../common/types';
import { messages } from '../../../../common/ui/invited-app-shortcuts/messages';
import { InvitedAppShortcutsContext } from '../../../../controllers/invited-app-shortcuts-context';

interface InvitedAppShortcutLabelProps {
	apsKey: InvitedAppShortcutAllowedApsKey;
	label: string;
}

const styles = cssMap({
	container: {
		display: 'grid',
		gridTemplateColumns: 'auto 1fr',
		alignItems: 'center',
		width: '100%',
	},
	lozengeContainer: {
		display: 'flex',
		justifyContent: 'flex-end',
		alignItems: 'center',
		minWidth: '0px',
	},
});

export const InvitedAppShortcutLabel = ({ label, apsKey }: InvitedAppShortcutLabelProps) => {
	const { impressedProducts } = useContext(InvitedAppShortcutsContext);
	const hasAlreadySeenThisInvitedAppShortcut = impressedProducts[apsKey];
	const [AnimatedLozengeComponent, setAnimatedLozengeComponent] =
		useState<ComponentType<any> | null>(null);

	useEffect(() => {
		// Keep SSR and initial hydration light by rendering static Lozenge first.
		if (hasAlreadySeenThisInvitedAppShortcut) {
			return;
		}
		// Load animation code only for users who can actually see the animated treatment.
		void import('./animated-lozenge/main').then((module) => {
			setAnimatedLozengeComponent(module.default as ComponentType<any>);
		});
	}, [hasAlreadySeenThisInvitedAppShortcut]);

	const LozengeComponent =
		!hasAlreadySeenThisInvitedAppShortcut && AnimatedLozengeComponent
			? AnimatedLozengeComponent
			: Lozenge;

	return (
		<div css={styles.container}>
			<Text>{label}</Text>
			<div css={styles.lozengeContainer}>
				<LozengeComponent appearance="inprogress">
					<FormattedMessage {...messages.invitedLozenge} />
				</LozengeComponent>
			</div>
		</div>
	);
};
